import { Routes } from '@angular/router';

import { authGuard } from './core/auth/auth.guard';
import { AuthCallbackComponent } from './core/auth/auth-callback.component';
import { LoginPage } from './core/auth/login/login.page';
import { permissionGuard } from './core/auth/permission.guard';
import { LayoutComponent } from './layout/layout.component';

/**
 * Root routing table (Implementation Architecture & Sprint Plan §10). Every authenticated
 * route nests under the LayoutComponent shell, guarded by authGuard. Conversations (Sprint
 * 2B) and Ticketing (Phase 3B) are the feature modules added so far -- Templates/
 * Dashboard/Notification-center/Administration routes arrive with their own phases.
 */
export const routes: Routes = [
  {
    path: 'auth/callback',
    component: AuthCallbackComponent,
  },
  {
    path: 'login',
    component: LoginPage,
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'conversations',
        canActivate: [permissionGuard('Conversations.View')],
        loadComponent: () =>
          import('./features/conversations/shell/conversations-shell.page').then((m) => m.ConversationsShellPage),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/conversations/empty/conversations-empty.page').then((m) => m.ConversationsEmptyPage),
          },
          {
            path: ':conversationId',
            loadComponent: () =>
              import('./features/conversations/thread/conversation-thread.page').then((m) => m.ConversationThreadPage),
          },
        ],
      },
      {
        path: 'tickets',
        canActivate: [permissionGuard('Tickets.View')],
        children: [
          {
            path: '',
            loadComponent: () => import('./features/ticketing/board/ticket-board.page').then((m) => m.TicketBoardPage),
          },
          {
            path: ':ticketId',
            loadComponent: () => import('./features/ticketing/details/ticket-details.page').then((m) => m.TicketDetailsPage),
          },
        ],
      },
      {
        path: 'templates',
        canActivate: [permissionGuard('Templates.View')],
        children: [
          {
            path: '',
            loadComponent: () => import('./features/templates/list/template-list.page').then((m) => m.TemplateListPage),
          },
          {
            path: ':templateId',
            loadComponent: () => import('./features/templates/details/template-details.page').then((m) => m.TemplateDetailsPage),
          },
        ],
      },
      {
        path: 'administration/whatsapp-accounts',
        canActivate: [permissionGuard('Administration.WhatsAppAccounts.View')],
        loadComponent: () =>
          import('./features/administration/whatsapp-accounts/whatsapp-accounts.page').then((m) => m.WhatsAppAccountsPage),
      },
      {
        // Ticket Types moved into the "Ticketing Configuration" page as a tab -- kept as a
        // redirect so any existing bookmark/link still lands somewhere valid.
        path: 'administration/ticket-types',
        redirectTo: 'administration/ticketing-config',
      },
      {
        path: 'administration/system-configuration',
        canActivate: [permissionGuard('Administration.SystemConfiguration.View')],
        loadComponent: () =>
          import('./features/administration/system-configuration/system-configuration.page').then((m) => m.SystemConfigurationPage),
      },
      {
        path: 'administration/ticketing-config',
        canActivate: [permissionGuard('Administration.Departments.Manage')],
        loadComponent: () =>
          import('./features/administration/ticketing-config/ticketing-config.page').then((m) => m.TicketingConfigPage),
      },
      {
        path: 'administration/webchat-config',
        canActivate: [permissionGuard('Administration.WebChatWidget.Manage')],
        loadComponent: () => import('./features/administration/webchat-config/webchat-config.page').then((m) => m.WebChatConfigPage),
      },
      {
        path: 'administration/provider-diagnostics',
        canActivate: [permissionGuard('Administration.ProviderDiagnostics.View')],
        loadComponent: () =>
          import('./features/administration/provider-diagnostics/provider-diagnostics.page').then((m) => m.ProviderDiagnosticsPage),
      },
      {
        path: 'dashboard',
        canActivate: [permissionGuard('Tickets.ViewReports')],
        loadComponent: () => import('./features/dashboard/dashboard.page').then((m) => m.DashboardPage),
      },
    ],
  },
];
